/**
 * Utility functions for working with markdown content
 */

/**
 * Strips markdown formatting to return plain text
 * @param {string} markdown - The markdown text to strip
 * @returns {string} - Plain text without markdown formatting
 */
export const stripMarkdown = (markdown) => {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  let text = markdown;

  // Remove code blocks (``` or ` blocks)
  text = text.replace(/```[\s\S]*?```/g, ''); // Multi-line code blocks
  text = text.replace(/`([^`]+)`/g, '$1'); // Inline code

  // Remove headers (# ## ### etc.)
  text = text.replace(/^#{1,6}\s+/gm, '');

  // Remove bold and italic (*bold*, **bold**, _italic_, __italic__)
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1'); // **bold**
  text = text.replace(/\*([^*]+)\*/g, '$1'); // *italic*
  text = text.replace(/__([^_]+)__/g, '$1'); // __bold__
  text = text.replace(/_([^_]+)_/g, '$1'); // _italic_

  // Remove strikethrough (~~text~~)
  text = text.replace(/~~([^~]+)~~/g, '$1');

  // Remove links [text](url) -> text
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Remove images ![alt](url) -> alt
  text = text.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');

  // Remove horizontal rules (--- or ***)
  text = text.replace(/^[-*]{3,}$/gm, '');

  // Remove blockquotes (> text)
  text = text.replace(/^>\s*/gm, '');

  // Remove list markers (- * + 1. 2. etc.)
  text = text.replace(/^[\s]*[-*+]\s+/gm, ''); // Unordered lists
  text = text.replace(/^[\s]*\d+\.\s+/gm, ''); // Ordered lists

  // Remove table formatting (| text |)
  text = text.replace(/\|/g, ' '); // Remove table pipes
  text = text.replace(/^[-:|\s]+$/gm, ''); // Remove table separator lines

  // Clean up extra whitespace
  text = text.replace(/\n\s*\n/g, '\n'); // Multiple line breaks to single
  text = text.replace(/^\s+|\s+$/g, ''); // Trim start/end
  text = text.replace(/[ \t]+/g, ' '); // Multiple spaces to single

  return text;
};

/**
 * Converts markdown to a more readable plain text format
 * Preserves some structure while removing formatting
 * @param {string} markdown - The markdown text to convert
 * @returns {string} - Readable plain text
 */
export const markdownToReadableText = (markdown) => {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  let text = markdown;

  // Convert headers to text with minimal formatting
  text = text.replace(/^#{1,6}\s+(.+)$/gm, '$1'); // Headers become plain text

  // Convert bold/italic to plain text but keep content
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1'); // **bold**
  text = text.replace(/\*([^*]+)\*/g, '$1'); // *italic*
  text = text.replace(/__([^_]+)__/g, '$1'); // __bold__
  text = text.replace(/_([^_]+)_/g, '$1'); // _italic_

  // Convert links to readable format: [text](url) -> text (url)
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');

  // Convert images to alt text: ![alt](url) -> [Image: alt]
  text = text.replace(/!\[([^\]]*)\]\([^)]+\)/g, (match, alt) => 
    alt ? `[Image: ${alt}]` : '[Image]'
  );

  // Convert code blocks to indicate code content
  text = text.replace(/```[\s\S]*?```/g, '[Code Block]');
  text = text.replace(/`([^`]+)`/g, '$1'); // Inline code keeps content

  // Clean up lists but preserve structure
  text = text.replace(/^[\s]*[-*+]\s+/gm, '• '); // Unordered lists
  text = text.replace(/^[\s]*(\d+)\.\s+/gm, '$1. '); // Ordered lists

  // Convert blockquotes
  text = text.replace(/^>\s*/gm, '');

  // Clean up whitespace
  text = text.replace(/\n\s*\n\s*\n/g, '\n\n'); // Max 2 line breaks
  text = text.replace(/^\s+|\s+$/g, ''); // Trim

  return text;
};